import { h, Component, State, Element } from "@stencil/core";

const getCurrentImageSource = (pictureElement: HTMLPictureElement): string => {
  const imgElement = pictureElement.querySelector('img');
  return imgElement?.currentSrc || '';
};

/**
 * Place your most important content in a prominent space, often at the top of your website.
 *
 * @slot start - Content to display at the start (top) of the hero.
 * 
 * @prop --pl-viewport-margins-max-width: The maximum width of the hero inner content.
 * @prop --pl-viewport-margins-gutter: The gutter width of the hero inner content.
 */
@Component({
  tag: 'pennlibs-hero',
  styleUrl: 'pennlibs-hero.css',
  shadow: true,
  assetsDirs: ['assets']
})
export class Hero {
  @Element() hostElement: HTMLElement;
  @State() heroPictureElement: null | HTMLPictureElement = null;
  @State() heroHeadingElement: null | HTMLHeadingElement = null;
  @State() heroParagraphElement: null | HTMLParagraphElement = null;
  @State() heroSrc: string = "";
  private lastSrc: string = "";
  private animationFrameId: number;

  componentWillLoad() {
    const heroPictureElement = this.hostElement.querySelector('picture[hero=art-direction]') as HTMLPictureElement;
    if (heroPictureElement) {
      this.heroPictureElement = heroPictureElement;
      this.startWatchingCurrentSrc(heroPictureElement);
    }

    const heroHeadingElement = this.hostElement.querySelector('h1[hero=heading]') as HTMLHeadingElement;
    if (heroHeadingElement) {
      this.heroHeadingElement = heroHeadingElement;
    }

    const heroParagraphElement = this.hostElement.querySelector('p[hero=sub-heading]') as HTMLParagraphElement;
    if (heroParagraphElement) {
      this.heroParagraphElement = heroParagraphElement;
    }
  }

  private startWatchingCurrentSrc(pictureElement: HTMLPictureElement) {
    const checkCurrentSrc = () => {
      const currentSrc = getCurrentImageSource(pictureElement);
      if (currentSrc !== this.lastSrc) {
        this.lastSrc = currentSrc;
        this.heroSrc = currentSrc;
      }
      this.animationFrameId = requestAnimationFrame(checkCurrentSrc);
    };
    
    this.animationFrameId = requestAnimationFrame(checkCurrentSrc);
  }

  disconnectedCallback() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
  }

  render() {
    return (
      <div class="hero" style={{ backgroundImage: `url(${this.heroSrc})` }}>
        <div class="hero__content">
          <slot name="start" />
          <div class="hero__heading-container">
            {this.heroHeadingElement && (
              <div class="viewport-margins">
                <h1 class="hero__heading" innerHTML={this.heroHeadingElement.innerText} />
                {this.heroParagraphElement && <p class="hero__sub-heading" innerHTML={this.heroParagraphElement.innerHTML} />}
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }
}